import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, UserCheck, MapPin, Lock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { backendUrl } from "@/components/SongCard";
import { PerfCard } from "./Community";
import { Button } from "@/components/ui/button";

export default function Profile({ handle }) {
  const { username: pu } = useParams();
  const username = handle || pu;
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [following, setFollowing] = useState(false);

  const load = () => api.get(`/users/${username}`).then(({ data }) => { setP(data); setFollowing(data.is_following); }).catch(() => setP(false));
  useEffect(() => { load(); }, [username]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Log in to follow singers."); return; }
    try { const { data } = await api.post(`/users/${username}/follow`); setFollowing(data.following); setP({ ...p, followers: p.followers + (data.following ? 1 : -1) }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (p === false) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">User not found.</div>;
  if (!p) return <div className="min-h-[50vh]" />;
  const isMe = user?.username === p.username;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="glass rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6 mb-8">
        <div className="w-28 h-28 rounded-full overflow-hidden bg-neon-purple/30 flex items-center justify-center text-4xl font-head font-bold glow-pink flex-shrink-0">
          {p.avatar_url ? <img src={backendUrl(p.avatar_url)} className="w-full h-full object-cover" alt="" /> : (p.display_name || p.username)[0].toUpperCase()}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h1 className="font-head text-3xl font-black tracking-tight">{p.display_name}</h1>
          <p className="text-muted-foreground">@{p.username}</p>
          {p.bio && <p className="text-sm mt-2 max-w-md">{p.bio}</p>}
          <div className="flex gap-6 justify-center sm:justify-start mt-3 text-sm">
            <span><b className="font-head">{p.recordings?.length || 0}</b> <span className="text-muted-foreground">songs</span></span>
            <span data-testid="follower-count"><b className="font-head">{p.followers}</b> <span className="text-muted-foreground">followers</span></span>
            <span><b className="font-head">{p.following}</b> <span className="text-muted-foreground">following</span></span>
          </div>
          {p.country && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1 justify-center sm:justify-start"><MapPin className="w-3 h-3" /> {p.country}</p>}
        </div>
        {!isMe && (
          <Button onClick={toggleFollow} data-testid="follow-btn" className={`rounded-full font-bold px-6 ${following ? "glass border border-white/20" : "bg-neon-pink hover:bg-neon-pink/90"}`}>
            {following ? <><UserCheck className="w-4 h-4 mr-2" /> Following</> : <><UserPlus className="w-4 h-4 mr-2" /> Follow</>}
          </Button>
        )}
        {isMe && <Link to="/settings"><Button variant="outline" className="rounded-full glass border-white/20">Edit profile</Button></Link>}
      </div>

      {p.is_private ? (
        <div className="card-surface rounded-2xl p-12 text-center text-muted-foreground"><Lock className="w-8 h-8 mx-auto mb-3 opacity-40" />This profile is private.</div>
      ) : p.recordings?.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center text-muted-foreground">No public performances yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {p.recordings.map((r) => <PerfCard key={r.recording_id} perf={{ ...r, owner: { username: p.username, display_name: p.display_name, avatar_url: p.avatar_url } }} />)}
        </div>
      )}
    </div>
  );
}
